// Impression Share Loss Diagnosis — Bidding & Budgets section, deterministic check.
// Shows the IS-loss table per Search campaign with budget vs rank split, so the auditor can
// see at a glance whether IS leakage is a budget problem (raise budget / loosen target) or
// a competitiveness problem (raise bids / improve QS / loosen target).

import type { EvidenceProps } from './registry';
import { pct } from '../../utils/format';

interface CampaignIsRow {
  id?: string;
  name?: string;
  impressions: number;
  isShare: number;
  budgetLost: number;
  rankLost: number;
  totalLost: number;
  budgetCapped: boolean;
  rankLagging: boolean;
}

interface IsLossShape {
  totalAnalyzedCampaigns: number;
  skippedBelowImpressionThreshold: number;
  impressionThreshold: number;
  thresholds: {
    significant: number;
    moderate: number;
    severe: number;
  };
  flaggedCount: number;
  budgetCappedCount: number;
  rankLaggingCount: number;
  campaigns: CampaignIsRow[];
}

function fmtInt(n: number): string {
  return n.toLocaleString();
}

export const ImpressionShareLossEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as IsLossShape;

  return (
    <div className='evidence'>
      <p className='evidence__lead'>
        <strong>{e.totalAnalyzedCampaigns}</strong> enabled Search campaign
        {e.totalAnalyzedCampaigns === 1 ? '' : 's'} analyzed (campaigns under{' '}
        {fmtInt(e.impressionThreshold)} impressions/30d skipped — IS metrics are unreliable below
        that). <strong>{e.flaggedCount}</strong> losing ≥ {pct(e.thresholds.significant)} of
        impression share: <strong>{e.budgetCappedCount}</strong> budget-capped,{' '}
        <strong>{e.rankLaggingCount}</strong> rank-lagging.
      </p>

      <table className='evidence__table evidence__table--impression-share'>
        <thead>
          <tr>
            <th>Campaign</th>
            <th className='evidence__th--right'>Impressions</th>
            <th className='evidence__th--right'>IS share</th>
            <th className='evidence__th--right'>Lost to budget</th>
            <th className='evidence__th--right'>Lost to rank</th>
          </tr>
        </thead>
        <tbody>
          {e.campaigns.map(c => {
            const anyFlagged = c.budgetCapped || c.rankLagging;
            return (
              <tr
                key={c.id ?? c.name}
                className={anyFlagged ? 'evidence__row--flagged' : undefined}
              >
                <td>{c.name ?? '(unnamed)'}</td>
                <td className='evidence__td--right'>{fmtInt(c.impressions)}</td>
                <td className='evidence__td--right'>{pct(c.isShare)}</td>
                <td className='evidence__td--right'>
                  {c.budgetCapped ? <strong>{pct(c.budgetLost)}</strong> : pct(c.budgetLost)}
                </td>
                <td className='evidence__td--right'>
                  {c.rankLagging ? <strong>{pct(c.rankLost)}</strong> : pct(c.rankLost)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className='evidence__footnote'>
        Severity thresholds: ≥ {pct(e.thresholds.significant)} lost = significant (small); ≥{' '}
        {pct(e.thresholds.moderate)} = moderate (medium); ≥ {pct(e.thresholds.severe)} = severe
        (large). Budget loss → raise budget or loosen target CPA/ROAS. Rank loss → improve Quality
        Score / raise bids / loosen target. Both are configuration-driven (the audit&apos;s scope),
        not performance commentary.
      </p>
    </div>
  );
};
